/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from '@angular/common';
import { Component, inject, input, WritableSignal, model } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Icon } from '../icon/icon.component';

/**
 * @name FileDownloadComponent
 * @description 브라우저에서 파일 다운로드를 지원하는 컴포넌트
 */
@Component({
  selector: 'app-file-download',
  imports: [Icon, CommonModule],
  template: `
    <button (click)="downloadFile($event)" class="w-full">
      @if (variant() === 'notice') {
      <!-- Notice 스타일 -->
      <div
        class="cursor-pointer flex items-center justify-between py-4 px-5 bg-zinc-50 border border-zinc-100 rounded-xl group"
      >
        <div class="flex items-center gap-3 flex-1">
          <app-icon
            [name]="
              isLoading()
                ? 'line-md:downloading-loop'
                : 'material-symbols:lab-profile'
            "
            class="size-5! text-zinc-300 shrink-0"
          />
          <span class="text-zinc-800 font-medium truncate">{{
            filename()
          }}</span>
          <span class=" text-zinc-400 font-medium shrink-0">
            {{ fileSizeFormat(size()) }}
          </span>
        </div>
        <app-icon
          [name]="
            isLoading()
              ? 'line-md:downloading-loop'
              : 'material-symbols:download-rounded'
          "
          class="size-5! text-zinc-500 shrink-0"
        />
      </div>
      } @else {
      <!-- 기본 스타일 -->
      <div
        class="overflow-hidden justify-between bg-white rounded-md w-full text-xs text-gray-500 cursor-pointer flex gap-3 items-center border border-primary-500 p-3 min-w-50"
      >
        <div class="flex gap-2 items-center">
          <app-icon
            [name]="
              isLoading() ? 'line-md:downloading-loop' : 'line-md:downloading'
            "
          />
          <p class="truncate max-w-20">{{ filename() }}</p>
        </div>
        <p>{{ fileSizeFormat(size()) }}</p>
      </div>
      }
    </button>
  `,
})
export default class FileDownloadComponent {
  readonly toastService = inject(ToastrService);

  url = input.required<string>();
  size = input<number>(0);
  filename = input<string>('file');
  variant = input<'default' | 'notice'>('default'); // 👈 variant 추가

  /**
   * @description 파일 다운로드 로딩상태
   */
  isLoading: WritableSignal<boolean> = model<boolean>(false);

  /**
   * @name downloadFile
   * @description 브라우저에서 파일 다운로드
   */
  async downloadFile(ev?: Event): Promise<void> {
    this.isLoading.set(true);
    try {
      await this.downloadForWeb();
    } catch (error) {
      this.toastService.error('다운로드에 실패했습니다.');
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * @name downloadForWeb
   * @description 웹 브라우저에서 파일 다운로드
   */
  private async downloadForWeb(): Promise<void> {
    if (!this.url()) {
      this.toastService.error('다운로드할 URL이 없습니다.');
      return;
    }

    const url = await this.getDownloadUrl();
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = this.filename();
    document.body.appendChild(a);
    a.click();

    // revokeObjectURL 지연
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      a.remove();
    }, 1000);
  }

  /**
   * @name getDownloadUrl
   * @description 다운로드용 URL 생성
   */
  private async getDownloadUrl(): Promise<string> {
    try {
      const res = await fetch(this.url(), {
        method: 'GET',
        headers: { 'Cache-Control': 'no-cache' },
        mode: 'cors',
        referrerPolicy: 'no-referrer-when-downgrade',
      });
      const blob = await res.blob();
      return window.URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error getting download URL:', error);
      return this.url();
    }
  }

  public fileSizeFormat(size: number): string {
    if (size < 1024) return `${size}B`;
    else if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)}KB`;
    else if (size < 1024 * 1024 * 1024)
      return `${(size / (1024 * 1024)).toFixed(0)}MB`;
    else return `${(size / (1024 * 1024 * 1024)).toFixed(2)}GB`;
  }
}
